import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdAdd,
  MdOutlineSearch,
  MdOutlineBadge,
  MdOutlineGroups,
  MdOutlineBlock,
  MdOutlineAdminPanelSettings,
  MdOutlineEdit,
  MdOutlineLogin,
  MdOutlineWhatsapp,
  MdCheck,
} from "react-icons/md";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { APP_LOGIN_URL, PROFESSIONALS } from "../../config/apiConfig";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import Modal from "../modals/Modal";
import ProfessionalForm from "../forms/ProfessionalForm";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
  SkeletonRows,
} from "../crm/Primitives";
import {
  SkeletonKpiStrip,
  SkeletonTable,
} from "../crm/Skeletons";
import {
  formatDate,
  formatNumber,
  initialsOf,
  whatsappLink,
} from "../crm/crmHelpers";

/*
  Usuários dos salões (a tabela de `Professionals`).

  A versão anterior tinha três problemas que não eram de estilo:

  1. **O filtro de situação mentia.** "Inativos" filtrava no navegador, sobre
     os 15 registros da página, enquanto o contador vinha do banco inteiro —
     dava para ver "2 resultados" com o cartão dizendo 37. Agora o filtro vai
     para o servidor (`status=active|inactive`, que eu precisei acrescentar lá).

  2. **A busca só rodava no submit** de um formulário, então digitar e esperar
     não fazia nada até apertar Enter.

  3. **A API devolvia o hash bcrypt de cada profissional** para o navegador.
     Corrigido no service; não era desta tela, mas aparecia por causa dela.
*/

const PERMISSOES = {
  ADMIN: { label: "Administrador", tone: "info" },
  POWER_USER: { label: "Power user", tone: "danger" },
  USER_CALENDARS: { label: "Vê agendas", tone: "neutral" },
  USER: { label: "Comum", tone: "neutral" },
};

const FILTROS = [
  { value: "", label: "Todos" },
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" },
];

const Kpi = ({ icon: Icon, label, value, hint, tone = "neutral" }) => (
  <div className="flex flex-1 items-start gap-3 border-slate-200 px-5 py-4 sm:border-r sm:last:border-r-0">
    <span
      className={`shrink-0 rounded-xl p-2 ${
        tone === "success"
          ? "bg-emerald-50 text-emerald-600"
          : tone === "danger"
          ? "bg-rose-50 text-rose-600"
          : tone === "info"
          ? "bg-sky-50 text-sky-600"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      <Icon size={18} />
    </span>
    <span className="min-w-0">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>
      <span className="block text-xl font-bold leading-tight text-slate-900">
        {value}
      </span>
      {hint ? (
        <span className="block text-[11px] text-slate-500">{hint}</span>
      ) : null}
    </span>
  </div>
);

const ProfessionalsTable = () => {
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("");
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [modoDrawer, setModoDrawer] = useState("EDIT");
  const [selecionado, setSelecionado] = useState(null);
  const [alvoStatus, setAlvoStatus] = useState(null);
  const [erroStatus, setErroStatus] = useState("");

  const buscaDebounced = useDebouncedValue(busca, 400);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ["professionals", pagina, buscaDebounced, status],
    async () => {
      const params = new URLSearchParams({
        page: String(pagina),
        searchName: buscaDebounced,
      });
      if (status) params.set("status", status);

      const { data: resposta } = await axios.get(
        `${PROFESSIONALS.LIST}?${params.toString()}`
      );
      return resposta.data;
    },
    { keepPreviousData: true }
  );

  const alterarStatus = useMutation(
    ({ id, isActive }) =>
      axios.put(`${PROFESSIONALS.ONE}/${id}/status`, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["professionals"]);
        setAlvoStatus(null);
      },
      onError: (erro) =>
        setErroStatus(
          erro?.response?.data?.message ||
            "Não foi possível alterar a situação do profissional."
        ),
    }
  );

  /*
    Entrar no app como a profissional, para dar suporte vendo o que ela vê.
    Abre em aba nova para não derrubar a sessão do painel.

    POST, e não o GET do cadastro: a chamada CRIA uma sessão e grava quem a
    abriu na trilha de auditoria. O erro agora aparece na tela — antes um
    token ausente virava `?token=undefined` e a aba nova abria na tela de
    login do app, sem explicar nada a quem clicou.
  */
  const entrarComo = async (profissional) => {
    setErroStatus("");

    try {
      const { data: resposta } = await axios.post(
        `${PROFESSIONALS.IMPERSONATE}/${profissional.id}/impersonate`
      );

      window.open(
        `${APP_LOGIN_URL}?token=${resposta.data.token}`,
        "_blank",
        "noopener"
      );
    } catch (erro) {
      setErroStatus(
        erro?.response?.data?.message ||
          "Não foi possível abrir a sessão de suporte."
      );
    }
  };

  const abrirDrawer = (modo, profissional = null) => {
    setModoDrawer(modo);
    setSelecionado(profissional);
    setDrawerAberto(true);
  };

  const lista = data?.data || [];
  const resumo = data?.statusSummary || { active: 0, inactive: 0 };
  const admins = lista.filter((p) => p.permissionId === "ADMIN").length;

  return (
    <>
      <div className="space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              Usuários dos salões
            </h1>
            <p className="text-sm text-slate-500">
              Quem acessa o app em cada cliente — permissão, situação e acesso
              de suporte.
            </p>
          </div>

          <button
            type="button"
            onClick={() => abrirDrawer("CREATE")}
            className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-pink-700"
          >
            <MdAdd size={18} /> Novo usuário
          </button>
        </header>

        <Card className="overflow-hidden">
          <div className="flex flex-col divide-y divide-slate-100 sm:flex-row sm:divide-y-0">
            <Kpi
              icon={MdOutlineGroups}
              label="Total"
              value={formatNumber(resumo.active + resumo.inactive)}
              hint="usuários cadastrados"
            />
            <Kpi
              icon={MdOutlineBadge}
              label="Ativos"
              value={formatNumber(resumo.active)}
              hint="podem entrar no app"
              tone="success"
            />
            <Kpi
              icon={MdOutlineBlock}
              label="Inativos"
              value={formatNumber(resumo.inactive)}
              hint="acesso bloqueado"
              tone={resumo.inactive ? "danger" : "neutral"}
            />
            <Kpi
              icon={MdOutlineAdminPanelSettings}
              label="Administradores"
              value={formatNumber(admins)}
              hint="nesta página"
              tone="info"
            />
          </div>
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <MdOutlineSearch
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar por nome, e-mail ou telefone"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm shadow-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />
          </div>

          <div className="flex gap-1.5">
            {FILTROS.map((item) => (
              <button
                key={item.value || "todos"}
                type="button"
                onClick={() => {
                  setStatus(item.value);
                  setPagina(1);
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition ${
                  status === item.value
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-300"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="overflow-hidden">
          {isLoading ? (
            <SkeletonTable rows={8} cols={5} />
          ) : error ? (
            <ErrorBlock
              message="Não foi possível carregar os usuários."
              onRetry={refetch}
            />
          ) : !lista.length ? (
            <EmptyState
              icon={MdOutlineBadge}
              title="Nenhum usuário no recorte"
              description="Limpe a busca ou troque o filtro de situação."
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[840px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3">Usuário</th>
                      <th className="px-4 py-3">Salão</th>
                      <th className="px-4 py-3">Permissão</th>
                      <th className="px-4 py-3">Situação</th>
                      <th className="px-4 py-3">Cadastro</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lista.map((profissional) => {
                      const permissao =
                        PERMISSOES[profissional.permissionId] || PERMISSOES.USER;
                      const zap = whatsappLink(profissional.phoneNumber);
                      const nomeCompleto = [profissional.name, profissional.surname]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <tr
                          key={profissional.id}
                          className={`hover:bg-slate-50/60 ${
                            profissional.isActive ? "" : "opacity-60"
                          }`}
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2.5">
                              {profissional.imageUrl ? (
                                <img
                                  src={profissional.imageUrl}
                                  alt=""
                                  className="h-8 w-8 shrink-0 rounded-lg object-cover"
                                />
                              ) : (
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-[10px] font-bold text-pink-700">
                                  {initialsOf(nomeCompleto)}
                                </span>
                              )}
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-slate-900">
                                  {nomeCompleto}
                                </p>
                                <p className="truncate text-[11px] text-slate-500">
                                  {profissional.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-2.5">
                            {profissional.companyId ? (
                              <Link
                                to={`/clientes/${profissional.companyId}`}
                                className="block truncate text-slate-700 hover:text-pink-700"
                              >
                                {profissional.Company?.name || `#${profissional.companyId}`}
                              </Link>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>

                          <td className="px-4 py-2.5">
                            <Badge tone={permissao.tone}>{permissao.label}</Badge>
                          </td>

                          <td className="px-4 py-2.5">
                            <Badge tone={profissional.isActive ? "success" : "danger"}>
                              {profissional.isActive ? "Ativo" : "Inativo"}
                            </Badge>
                          </td>

                          <td className="px-4 py-2.5 text-slate-600">
                            {formatDate(profissional.createdAt)}
                          </td>

                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              {zap ? (
                                <a
                                  href={zap}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Abrir WhatsApp"
                                  className="rounded-lg p-1.5 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600"
                                >
                                  <MdOutlineWhatsapp size={17} />
                                </a>
                              ) : null}
                              <button
                                type="button"
                                title="Entrar no app como este usuário"
                                onClick={() => entrarComo(profissional)}
                                className="rounded-lg p-1.5 text-slate-300 transition hover:bg-sky-50 hover:text-sky-600"
                              >
                                <MdOutlineLogin size={17} />
                              </button>
                              <button
                                type="button"
                                title="Editar"
                                onClick={() => abrirDrawer("EDIT", profissional)}
                                className="rounded-lg p-1.5 text-slate-300 transition hover:bg-pink-50 hover:text-pink-600"
                              >
                                <MdOutlineEdit size={17} />
                              </button>
                              <button
                                type="button"
                                title={
                                  profissional.isActive
                                    ? "Bloquear acesso"
                                    : "Reativar acesso"
                                }
                                onClick={() => {
                                  setErroStatus("");
                                  setAlvoStatus(profissional);
                                }}
                                className={`rounded-lg p-1.5 text-slate-300 transition ${
                                  profissional.isActive
                                    ? "hover:bg-rose-50 hover:text-rose-600"
                                    : "hover:bg-emerald-50 hover:text-emerald-600"
                                }`}
                              >
                                {profissional.isActive ? (
                                  <MdOutlineBlock size={17} />
                                ) : (
                                  <MdCheck size={17} />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-3">
                <span className="text-xs text-slate-500">
                  {formatNumber(data.totalCount)} usuário(s) · página{" "}
                  {data.currentPage} de {data.totalPages}
                  {isFetching ? " · atualizando…" : ""}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagina <= 1}
                    onClick={() => setPagina((p) => p - 1)}
                    className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
                  >
                    anterior
                  </button>
                  <button
                    type="button"
                    disabled={pagina >= data.totalPages}
                    onClick={() => setPagina((p) => p + 1)}
                    className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
                  >
                    próxima
                  </button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <Modal
        isOpen={Boolean(alvoStatus)}
        onClose={() => setAlvoStatus(null)}
        size="sm"
        icon={HiOutlineExclamationCircle}
        title={alvoStatus?.isActive ? "Bloquear acesso" : "Reativar acesso"}
        subtitle={alvoStatus ? `${alvoStatus.name} · ${alvoStatus.email}` : undefined}
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAlvoStatus(null)}
              className="flex-1 rounded-xl border border-slate-200 py-2 text-sm font-semibold text-slate-600"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={alterarStatus.isLoading}
              onClick={() =>
                alterarStatus.mutate({
                  id: alvoStatus.id,
                  isActive: !alvoStatus.isActive,
                })
              }
              className={`flex-1 rounded-xl py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                alvoStatus?.isActive
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {alterarStatus.isLoading
                ? "Salvando..."
                : alvoStatus?.isActive
                ? "Bloquear"
                : "Reativar"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          {alvoStatus?.isActive
            ? "O usuário perde o acesso ao app imediatamente. Os agendamentos dele continuam no sistema."
            : "Para o profissional voltar a receber agendamentos, reative também a agenda no cadastro."}
        </p>

        {erroStatus ? (
          <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200">
            {erroStatus}
          </p>
        ) : null}
      </Modal>

      {/*
        Modal, não gaveta.

        A gaveta ocupava metade da largura e empurrava o formulário para uma
        coluna estreita — as tabelas de comissão por serviço ficavam com
        rolagem horizontal própria dentro de uma área já estreita. O modal usa
        a largura inteira disponível e, de quebra, acerta o rodapé: o botão
        fixo do formulário usa `-mx-5`, que é exatamente o padding do corpo do
        modal (na gaveta, com `p-4`, ele estourava 4px de cada lado).
      */}
      <Modal
        isOpen={drawerAberto}
        onClose={setDrawerAberto}
        size="xl"
        icon={MdOutlineBadge}
        title={modoDrawer === "EDIT" ? "Editar usuário" : "Novo usuário"}
        subtitle={
          modoDrawer === "EDIT" && selecionado
            ? `${[selecionado.name, selecionado.surname].filter(Boolean).join(" ")} · ${
                selecionado.Company?.name || `#${selecionado.companyId}`
              }`
            : "Cadastro de usuário no salão"
        }
        bodyClassName="bg-slate-50/50"
      >
        <ProfessionalForm
          professional={selecionado}
          onClose={setDrawerAberto}
          isOpen={drawerAberto}
          fetchProfessionals={() =>
            queryClient.invalidateQueries(["professionals"])
          }
          type={modoDrawer}
        />
      </Modal>
    </>
  );
};

export default ProfessionalsTable;
